import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ogImage } from '../../lib/ogImage';
import { Instagram, ChevronRight } from 'lucide-react';
import BlogLayout from '../../components/BlogLayout';

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    { "@type": "Question", name: "Πόσο κοστίζει ένα τατουάζ στην Αθήνα;", acceptedAnswer: { "@type": "Answer", text: "Δεν υπάρχει ενιαία τιμή. Εξαρτάται από μέγεθος, πολυπλοκότητα, style και χρόνο συνεδρίας. Στείλε μας DM με το concept σου για εκτίμηση." } },
    { "@type": "Question", name: "Γιατί κάποια τατουάζ κοστίζουν περισσότερο;", acceptedAnswer: { "@type": "Answer", text: "Η εμπειρία του artist, η λεπτομέρεια του σχεδίου, ο χρόνος που απαιτείται και αν είναι custom design επηρεάζουν σημαντικά το κόστος." } },
    { "@type": "Question", name: "Πώς γίνεται η κοστολόγηση στο 210 Tattoo Athens;", acceptedAnswer: { "@type": "Answer", text: "Αξιολογούμε κάθε project ξεχωριστά. Στείλε μας ιδέα, μέγεθος και placement για ρεαλιστική εκτίμηση." } },
  ],
};

export default function TattooCostAthens() {
  useEffect(() => {
    const inject = (id: string, s: object) => {
      let tag = document.getElementById(id);
      if (!tag) { tag = document.createElement('script'); tag.id = id; (tag as HTMLScriptElement).type = 'application/ld+json'; document.head.appendChild(tag); }
      tag.textContent = JSON.stringify(s);
    };
    const articleSchema = {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": "Πόσο κοστίζει ένα τατουάζ στην Αθήνα; Οδηγός τιμών και παράγοντες",
      "description": "Μάθε τι επηρεάζει την τιμή ενός τατουάζ στην Αθήνα: μέγεθος, style, εμπειρία artist και custom design. Ρεαλιστικός οδηγός από το 210 Tattoo Athens.",
      "image": "https://210tattoo.gr/og-image.png",
      "datePublished": "2026-04-24",
      "dateModified": "2026-04-24",
      "author": { "@type": "Organization", "name": "210 Tattoo Athens", "url": "https://210tattoo.gr" },
      "publisher": { "@id": "https://210tattoo.gr/#tattooparlor" },
      "url": "https://210tattoo.gr/blog/tattoo-cost-athens",
      "inLanguage": "el"
    };
    inject('schema-tattoo-cost-faq', faqSchema);
    inject('schema-tattoo-cost-article', articleSchema);
    return () => {
      document.getElementById('schema-tattoo-cost-faq')?.remove();
      document.getElementById('schema-tattoo-cost-article')?.remove();
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>Πόσο κοστίζει ένα τατουάζ στην Αθήνα; Οδηγός τιμών | 210 Tattoo Athens</title>
        <meta name="description" content="Μάθε τι επηρεάζει την τιμή ενός τατουάζ στην Αθήνα: μέγεθος, style, εμπειρία artist και custom design. Ρεαλιστικός οδηγός από το 210 Tattoo Athens." />
        <link rel="canonical" href="https://210tattoo.gr/blog/tattoo-cost-athens" />
        <link rel="alternate" hrefLang="el" href="https://210tattoo.gr/blog/tattoo-cost-athens" />
        <link rel="alternate" hrefLang="x-default" href="https://210tattoo.gr/blog/tattoo-cost-athens" />
        <meta property="og:title" content="Πόσο κοστίζει ένα τατουάζ στην Αθήνα; Οδηγός τιμών | 210 Tattoo Athens" />
        <meta property="og:description" content="Μάθε τι επηρεάζει την τιμή ενός τατουάζ στην Αθήνα: μέγεθος, style, εμπειρία artist και custom design." />
        <meta property="og:url" content="https://210tattoo.gr/blog/tattoo-cost-athens" />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={ogImage('tattoo-cost-athens')} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Πόσο κοστίζει ένα τατουάζ στην Αθήνα; | 210 Tattoo Athens" />
        <meta name="twitter:description" content="Τι επηρεάζει την τιμή ενός τατουάζ στην Αθήνα. Οδηγός κοστολόγησης από το 210 Tattoo Athens." />
        <meta name="twitter:image" content={ogImage('tattoo-cost-athens')} />
      </Helmet>

      <BlogLayout>
        {/* Hero */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-pink-950/20 via-black to-black pointer-events-none" />
          <div className="max-w-4xl mx-auto relative z-10">
            <div className="flex items-center gap-2 text-pink-500 text-sm font-medium mb-6">
              <a href="/" className="hover:text-pink-400 transition-colors">Home</a>
              <ChevronRight size={14} />
              <span>Κόστος Τατουάζ Αθήνα</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              <span className="bg-gradient-to-r from-pink-500 via-pink-400 to-pink-300 bg-clip-text text-transparent">Οδηγός τιμών</span>
              <br />
              <span className="text-white">για τατουάζ στην Αθήνα</span>
            </h1>
            <p className="text-gray-500 text-xs mb-4 tracking-widest uppercase">24 Απριλίου 2026</p>
            <p className="text-xl text-gray-300 mb-10 leading-relaxed max-w-2xl">
              Μία από τις πιο συχνές ερωτήσεις πριν από ένα νέο τατουάζ: πόσο κοστίζει; Η τιμή διαμορφώνεται από συγκεκριμένους παράγοντες που αξίζει να γνωρίζεις πριν ψάξεις studio.
            </p>
            <a href="https://ig.me/m/210tattoo" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-pink-600 to-pink-700 hover:from-pink-700 hover:to-pink-800 px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 inline-flex items-center gap-3 shadow-2xl hover:shadow-pink-500/30 transform hover:scale-105">
              <Instagram size={20} />
              Κλείσε Ραντεβού
            </a>
          </div>
        </section>

        {/* Intro */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Γιατί δεν υπάρχει <span className="text-pink-500">ενιαία τιμή</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-6">
              Μία από τις πιο συχνές ερωτήσεις που κάνει κάποιος πριν κλείσει το πρώτο ή το επόμενο τατουάζ του είναι απλή: πόσο κοστίζει; Είναι λογικό. Θέλεις να ξέρεις τι budget χρειάζεται, τι επηρεάζει την τιμή και αν οι διαφορές από studio σε studio δικαιολογούνται.
            </p>
            <p className="text-gray-300 text-lg leading-relaxed mb-6">
              Η σύντομη απάντηση είναι ότι δεν υπάρχει μία ενιαία τιμή για ένα τατουάζ στην Αθήνα. Η σωστή απάντηση είναι ότι η τιμή διαμορφώνεται από συγκεκριμένους παράγοντες.
            </p>
            <p className="text-gray-300 text-lg leading-relaxed">
              Στο 210 Tattoo Athens, στην Αναστασίου Γενναδίου 34 στο Γκύζη, η κοστολόγηση γίνεται με βάση το ίδιο το project και όχι με αυθαίρετους αριθμούς.
            </p>
          </div>
        </section>

        {/* Factors */}
        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Τι επηρεάζει <span className="text-pink-500">την τιμή ενός τατουάζ</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Το μέγεθος είναι μόνο η αρχή. Ένα μικρό τατουάζ δεν σημαίνει απαραίτητα φθηνό τατουάζ. Ένα fine line σχέδιο με πολύ λεπτομέρεια, σωστές αποστάσεις, καθαρές γραμμές και ακρίβεια μπορεί να απαιτεί περισσότερη τεχνική από ένα μεγαλύτερο απλό σχέδιο.
            </p>
            <div className="space-y-4 mb-8">
              {[
                { title: 'Μέγεθος του σχεδίου', desc: 'Μεγαλύτερο σχέδιο σημαίνει περισσότερο χρόνο. Αλλά το μέγεθος από μόνο του δεν καθορίζει την τιμή.' },
                { title: 'Πολυπλοκότητα', desc: 'Λεπτομέρεια, διαβαθμίσεις, πολύπλοκες συνθέσεις απαιτούν υψηλότερο επίπεδο τεχνικής και συγκέντρωσης.' },
                { title: 'Style, realism, blackwork, fine line', desc: 'Κάθε στυλ έχει διαφορετικές απαιτήσεις. Το realism είναι συνήθως πιο χρονοβόρο από ένα minimalist line.' },
                { title: 'Placement στο σώμα', desc: 'Δύσκολες περιοχές όπως λαιμός, χέρια ή πλευρά απαιτούν περισσότερη τεχνική προσαρμογή.' },
                { title: 'Custom design', desc: 'Ένα custom τατουάζ περιλαμβάνει και τον χρόνο σχεδιασμού πριν τη συνεδρία. Δεν πληρώνεις μόνο τη βελόνα.' },
                { title: 'Cover up ή correction', desc: 'Τα cover ups απαιτούν ιδιαίτερη μελέτη σύνθεσης και τεχνική. Δεν αντιμετωπίζονται σαν συνηθισμένο τατουάζ.' },
              ].map((item, i) => (
                <div key={i} className="flex gap-4 items-start">
                  <span className="w-2 h-2 rounded-full bg-pink-500 shrink-0 mt-2" />
                  <div>
                    <p className="text-white font-semibold mb-1">{item.title}</p>
                    <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Size & time */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Μέγεθος, λεπτομέρεια <span className="text-pink-500">και χρόνος συνεδρίας</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-6">
              Πολλοί πιστεύουν ότι η τιμή ανεβαίνει μόνο όσο μεγαλώνει το σχέδιο. Δεν λειτουργεί έτσι.
            </p>
            <div className="space-y-4 mb-8">
              {[
                { title: 'Μικρό script τατουάζ', desc: 'Μπορεί να γίνει σχετικά γρήγορα. Κοστολογείται βάσει χρόνου και πολυπλοκότητας γραμμών.' },
                { title: 'Μικρό micro realism', desc: 'Ίσως χρειαστεί πολύ μεγαλύτερη συγκέντρωση και τεχνική από ένα απλό γραμμικό σχέδιο πολλαπλάσιου μεγέθους.' },
                { title: 'Sleeves, chest pieces, multi-session', desc: 'Για μεγάλα projects η κοστολόγηση σχετίζεται συνήθως με τον χρόνο. Εκεί η εμπειρία παίζει μεγάλο ρόλο, ένας έμπειρος artist δουλεύει αποδοτικά χωρίς να θυσιάζει ποιότητα.' },
              ].map((item, i) => (
                <div key={i} className="bg-gray-900 rounded-xl p-6 border border-pink-600/10">
                  <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Artist experience */}
        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Γιατί η εμπειρία του artist <span className="text-pink-500">παίζει ρόλο</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Αυτό είναι σημείο που πολλοί αγνοούν όταν συγκρίνουν μόνο τιμές. Δεν πληρώνεις μόνο το μελάνι.
            </p>
            <div className="grid sm:grid-cols-2 gap-4 mb-8">
              {[
                { title: 'Σχεδιαστική γνώση', desc: 'Η σύνθεση, οι αναλογίες και το placement δεν είναι τυχαία. Απαιτούν εμπειρία και αισθητική κατάρτιση.' },
                { title: 'Τεχνική εφαρμογή', desc: 'Σταθερό χέρι, σωστή πίεση, ρυθμός. Αυτά δεν μαθαίνονται γρήγορα.' },
                { title: 'Καθαρότητα γραμμών & saturation', desc: 'Η ποιότητα που φαίνεται αμέσως, και διαρκεί στον χρόνο.' },
                { title: 'Longevity του τατουάζ', desc: 'Ένα καλά εκτελεσμένο τατουάζ γερνά όμορφα. Ένα κακό χρειάζεται διόρθωση.' },
              ].map((item, i) => (
                <div key={i} className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200">
                  <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Cheap tattoo cost more */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Πότε ένα φθηνό τατουάζ <span className="text-pink-500">κοστίζει περισσότερο τελικά</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Αυτό το βλέπουμε συχνά σε cover up consultations. Κάποιος επέλεξε με μοναδικό κριτήριο τη χαμηλή τιμή, και μετά εμφανίστηκαν προβλήματα.
            </p>
            <div className="space-y-4 mb-8">
              {[
                { title: 'Blowouts', desc: 'Το μελάνι «φεύγει» εκτός γραμμής. Δύσκολο να διορθωθεί και άσχημο αισθητικά.' },
                { title: 'Αδύναμες γραμμές', desc: 'Χάνουν ορατότητα γρήγορα και απαιτούν rework.' },
                { title: 'Κακή σύνθεση και αναλογίες', desc: 'Δεν διορθώνεται εύκολα, χρειάζεται cover up ή laser.' },
                { title: 'Λάθος placement', desc: 'Αν δεν μελετηθεί πώς κινείται το σχέδιο με το σώμα, το αποτέλεσμα χαλάει γρήγορα.' },
              ].map((item, i) => (
                <div key={i} className="bg-gray-900 rounded-xl p-6 border border-pink-600/10">
                  <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
            <p className="text-gray-300 text-lg leading-relaxed">
              Όταν μιλάμε για κάτι μόνιμο στο σώμα, η λογική του cheapest option συνήθως δεν είναι η πιο οικονομική μακροπρόθεσμα.
            </p>
          </div>
        </section>

        {/* Budget ranges */}
        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Πόσο κοστίζουν συνήθως <span className="text-pink-500">διαφορετικά τατουάζ</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Χωρίς να υπάρχουν universal rates, κάθε κατηγορία project έχει διαφορετικές απαιτήσεις budget. Η σωστή προσέγγιση είναι consultation: όχι να ρωτήσεις μόνο "πόσο πάει ένα τατουάζ", αλλά να αξιολογηθεί το δικό σου project.
            </p>
            <div className="space-y-4">
              {[
                { title: 'Μικρό minimal τατουάζ', desc: 'Απλό γραμμικό ή script σε μικρή κλίμακα. Γρήγορο σε εκτέλεση, χαμηλότερο κόστος.' },
                { title: 'Medium custom piece', desc: 'Custom σχέδιο με λεπτομέρεια σε μέτριο μέγεθος. Περιλαμβάνει χρόνο σχεδιασμού.' },
                { title: 'Realism project', desc: 'Απαιτεί εξειδίκευση και περισσότερο χρόνο. Η τιμή αντικατοπτρίζει την τεχνική.' },
                { title: 'Sleeve ή multi-session composition', desc: 'Κοστολογείται ανά συνεδρία. Απαιτεί σχεδιαστικό πλάνο εκ των προτέρων.' },
                { title: 'Cover up', desc: 'Απαιτεί αξιολόγηση του υπάρχοντος τατουάζ. Η τιμή εξαρτάται από πολυπλοκότητα κάλυψης.' },
              ].map((item, i) => (
                <div key={i} className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200">
                  <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How we price */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Πώς γίνεται η κοστολόγηση <span className="text-pink-500">στο 210 Tattoo Athens</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Στο studio προτιμάμε να βλέπουμε το concept πρώτα. Αυτό προστατεύει και εσένα και το τελικό αποτέλεσμα.
            </p>
            <div className="space-y-6">
              {[
                { step: '01', title: 'Στέλνεις ιδέα ή references', desc: 'Εικόνες, σκίτσα, λεκτική περιγραφή. Ό,τι έχεις.' },
                { step: '02', title: 'Ορίζεις μέγεθος και placement', desc: 'Πού θες να μπει και πόσο μεγάλο σκέφτεσαι.' },
                { step: '03', title: 'Λαμβάνεις ρεαλιστική εκτίμηση', desc: 'Σε κατευθύνουμε βάσει του συγκεκριμένου project, όχι γενικών αριθμών.' },
              ].map((item) => (
                <div key={item.step} className="flex gap-6 items-start">
                  <div className="text-4xl font-bold text-pink-600/30 shrink-0 w-12 text-center leading-none pt-1">{item.step}</div>
                  <div className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 flex-1">
                    <h3 className="text-white font-semibold text-lg mb-2">{item.title}</h3>
                    <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* What not to do */}
        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Τι να μην κάνεις <span className="text-pink-500">όταν ψάχνεις τιμές</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Μην συγκρίνεις μόνο αριθμούς. Η τιμή χωρίς context δεν λέει πολλά.
            </p>
            <div className="space-y-4">
              {[
                { title: 'Portfolio', desc: 'Κοίτα τη δουλειά, όχι μόνο την τιμή. Ταιριάζει το στυλ του artist με αυτό που θέλεις;' },
                { title: 'Healed δουλειές', desc: 'Τα φρέσκα τατουάζ φαίνονται καλά σχεδόν παντού. Τα healed φανερώνουν την πραγματική ποιότητα.' },
                { title: 'Hygiene', desc: 'Ρώτα για αποστειρωμένα εργαλεία, single use needles, καθαρό setup. Ένα σοβαρό studio δεν ενοχλείται από τέτοιες ερωτήσεις.' },
                { title: 'Reviews', desc: 'Πραγματικές εμπειρίες πελατών λένε πολλά για επαγγελματισμό, επικοινωνία και αποτέλεσμα.' },
              ].map((item, i) => (
                <div key={i} className="flex gap-4 items-start">
                  <span className="w-2 h-2 rounded-full bg-pink-500 shrink-0 mt-2" />
                  <div>
                    <p className="text-white font-semibold mb-1">{item.title}</p>
                    <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Θέλεις εκτίμηση για <span className="text-pink-500">το project σου;</span></h2>
            <p className="text-gray-300 text-lg mb-8">
              Αν σκέφτεσαι νέο τατουάζ και θέλεις πραγματική εκτίμηση, στείλε μας DM στο Instagram του 210 Tattoo Athens και κλείσε ραντεβού.
            </p>
            <a href="https://ig.me/m/210tattoo" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-pink-600 to-pink-700 hover:from-pink-700 hover:to-pink-800 px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 inline-flex items-center gap-3 shadow-2xl hover:shadow-pink-500/30 transform hover:scale-105">
              <Instagram size={20} />
              Στείλε DM για εκτίμηση
            </a>
          </div>
        </section>

        {/* Related articles */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-white mb-8">Σχετικά <span className="text-pink-500">Άρθρα</span></h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <Link to="/blog/tattoo-artist-athens" className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200 group">
                <p className="text-pink-500 text-xs uppercase tracking-widest mb-2">Άρθρο</p>
                <h3 className="text-white font-semibold group-hover:text-pink-400 transition-colors">Πώς να διαλέξεις tattoo artist στην Αθήνα</h3>
                <p className="text-gray-400 text-sm mt-2 leading-relaxed">Τι να προσέξεις πριν επιλέξεις studio: portfolio, hygiene, consultation και red flags.</p>
              </Link>
              <Link to="/blog/first-tattoo-athens" className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200 group">
                <p className="text-pink-500 text-xs uppercase tracking-widest mb-2">Άρθρο</p>
                <h3 className="text-white font-semibold group-hover:text-pink-400 transition-colors">Το Πρώτο σου Τατουάζ στην Αθήνα</h3>
                <p className="text-gray-400 text-sm mt-2 leading-relaxed">Πλήρης οδηγός για πρωτάρηδες, από την επιλογή σχεδίου μέχρι την εκτέλεση.</p>
              </Link>
            </div>
          </div>
        </section>

        <section className="py-16 bg-gray-950 border-t border-pink-600/10">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-white mb-8 text-center">Υπηρεσίες <span className="text-pink-500">/ Services</span></h2>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="/fine-line-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Fine Line Tattoo Athens</a>
              <a href="/realistic-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Realistic Tattoo Athens</a>
              <a href="/cover-up-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Cover Up Tattoo Athens</a>
              <a href="/black-gray-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Black &amp; Gray Tattoo Athens</a>
              <a href="/custom-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Custom Tattoo Athens</a>
            </div>
          </div>
        </section>
      </BlogLayout>
    </>
  );
}
